import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import { ChannelInvoiceType } from "@/types/response/ChannelInvoiceType";
import { PagedResponse } from "@/types/response/PagedResponse";

type UseChannelInvoicesParams = {
  channelId: string;
  year: string;
  month: string;
  tenantId?: string | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
};

/**
 * Fetches channel invoices for a specific channel, year, and month.
 * Used for displaying invoiced channel data with pagination and filtering.
 */
export function useChannelInvoices(params: UseChannelInvoicesParams) {
  return useQuery<PagedResponse<ChannelInvoiceType>, AxiosError>({
    queryKey: invoiceQueryKeys.channels.detail(
      params.channelId,
      params.year,
      params.month,
      {
        tenantId: params.tenantId,
        descending: params.descending,
        orderBy: params.orderBy,
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
      }
    ),
    queryFn: () =>
      instance
        .get<PagedResponse<ChannelInvoiceType>>(
          `/api/v2/Channels/${params.channelId}/Invoices/${params.year}/${params.month}`,
          {
            params: {
              descending: params.descending,
              orderBy: params.orderBy,
              page: params.page,
              search: params.search,
              pageSize: params.pageSize,
              tenantId: params.tenantId || undefined,
            },
          }
        )
        .then((res) => res.data),
    enabled: !!params.channelId && !!params.year && !!params.month,
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    refetchOnMount: true, // Always refetch on mount to ensure fresh data after invalidation
    staleTime: 20 * 60 * 1000,
  });
}
